import { createRoute } from "@tanstack/react-router";
import { withPermission } from "../lib/with-permission";
import { useTranslation } from "react-i18next";
import { useCallback, useState } from "react";
import { rootRoute } from "./__root";
import { PageHeader } from "../components/ui/PageHeader/PageHeader";
import { Card } from "../components/ui/Card/Card";
import { Button } from "../components/ui/Button/Button";
import { Badge } from "../components/ui/Badge/Badge";
import { ConfirmDialog } from "../components/ui/ConfirmDialog/ConfirmDialog";
import { LoadingSpinner } from "../components/ui/LoadingSpinner/LoadingSpinner";
import { ErrorFallback } from "../components/ui/ErrorFallback/ErrorFallback";
import { BackButton } from "../components/ui/BackButton/BackButton";
import { Modal } from "../components/ui/Modal/Modal";
import { FormField } from "../components/ui/FormField/FormField";
import { Input } from "../components/ui/Input/Input";
import { Select } from "../components/ui/Select/Select";
import { SearchableSelect } from "../components/ui/SearchableSelect/SearchableSelect";
import type { SearchableSelectOption } from "../components/ui/SearchableSelect/SearchableSelect";
import { useActivityDetail, useUpdateActivity, useDeleteActivity } from "../hooks/use-activities";
import { ACTIVITY_TYPES, ACTIVITY_DIRECTIONS, ACTIVITY_OUTCOMES } from "@crm/shared/schemas";
import type { ActivityType, ActivityDirection, ActivityOutcome } from "@crm/shared/types";
import { api } from "../lib/api";
import { formatDate } from "../lib/date";
import styles from "./ActivityDetail.module.css";

export const route = createRoute({
	getParentRoute: () => rootRoute,
	path: "/activities/$id",
	component: withPermission(ActivityDetailPage, "activities", "read"),
});

function ActivityDetailPage() {
	const { id } = route.useParams();
	const { t } = useTranslation();
	const activityQuery = useActivityDetail(id);
	const updateActivity = useUpdateActivity();
	const deleteActivity = useDeleteActivity();
	const [deleteOpen, setDeleteOpen] = useState(false);

	const [editOpen, setEditOpen] = useState(false);
	const [form, setForm] = useState({
		type: "call" as ActivityType,
		subject: "",
		content: "",
		dueDate: "",
		companyId: "",
		personId: "",
		contactName: "",
		contactOrgName: "",
		contactPhone: "",
		contactEmail: "",
		location: "",
		direction: "" as ActivityDirection | "",
		outcome: "" as ActivityOutcome | "",
	});
	const [formError, setFormError] = useState("");
	const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
	const [initialOptions, setInitialOptions] = useState<Record<string, SearchableSelectOption>>({});
	const [newPersonOpen, setNewPersonOpen] = useState(false);
	const [newPerson, setNewPerson] = useState({
		firstName: "",
		lastName: "",
		title: "",
		phone: "",
		email: "",
	});
	const [newPersonError, setNewPersonError] = useState("");
	const [newPersonSaving, setNewPersonSaving] = useState(false);
	const [personsRefresh, setPersonsRefresh] = useState(0);

	const loadCompanies = useCallback(async (query: string) => {
		const res = await api.get<{ items: Array<{ id: string; legalName: string }> }>(
			`/api/companies?limit=50${query ? `&search=${encodeURIComponent(query)}` : ""}`,
		);
		return res.items.map((c) => ({ value: c.id, label: c.legalName }));
	}, []);

	const loadPersons = useCallback(
		async (query: string) => {
			if (!form.companyId) return [];
			const res = await api.get<{
				items: Array<{ id: string; firstName: string; lastName: string }>;
			}>(
				`/api/persons?limit=50&companyId=${form.companyId}${query ? `&search=${encodeURIComponent(query)}` : ""}`,
			);
			return res.items.map((p) => ({ value: p.id, label: `${p.firstName} ${p.lastName}` }));
		},
		[form.companyId],
	);

	if (activityQuery.isLoading) return <LoadingSpinner />;
	if (activityQuery.isError || !activityQuery.data)
		return <ErrorFallback onRetry={activityQuery.refetch} />;

	const activity = activityQuery.data;

	const typeOptions = ACTIVITY_TYPES.map((v) => ({ value: v, label: t(v) }));
	const directionOptions = ACTIVITY_DIRECTIONS.map((v) => ({ value: v, label: t(v) }));
	const outcomeOptions = ACTIVITY_OUTCOMES.map((v) => ({ value: v, label: t(v) }));

	const openEdit = () => {
		setForm({
			type: activity.type,
			subject: activity.subject,
			content: activity.content ?? "",
			dueDate: activity.dueDate ? activity.dueDate.slice(0, 10) : "",
			companyId: activity.companyId ?? "",
			personId: activity.personId ?? "",
			contactName: activity.contactName ?? "",
			contactOrgName: activity.contactOrgName ?? "",
			contactPhone: activity.contactPhone ?? "",
			contactEmail: activity.contactEmail ?? "",
			location: activity.location ?? "",
			direction: activity.direction ?? "",
			outcome: activity.outcome ?? "",
		});
		setInitialOptions({
			...(activity.companyId && activity.companyName
				? { company: { value: activity.companyId, label: activity.companyName } }
				: {}),
			...(activity.personId && activity.personName
				? { person: { value: activity.personId, label: activity.personName } }
				: {}),
		});
		setFieldErrors({});
		setFormError("");
		setNewPersonOpen(false);
		setEditOpen(true);
	};

	const saveNewPerson = async () => {
		setNewPersonError("");
		if (!newPerson.firstName.trim() || !newPerson.lastName.trim()) {
			setNewPersonError(t("requiredField"));
			return;
		}
		setNewPersonSaving(true);
		try {
			const created = await api.post<{ id: string; firstName: string; lastName: string }>(
				"/api/persons",
				{ ...newPerson, companyId: form.companyId },
			);
			const label = `${created.firstName} ${created.lastName}`;
			setInitialOptions((prev) => ({ ...prev, person: { value: created.id, label } }));
			setForm((prev) => ({ ...prev, personId: created.id }));
			setPersonsRefresh((n) => n + 1);
			setNewPerson({ firstName: "", lastName: "", title: "", phone: "", email: "" });
			setNewPersonOpen(false);
		} catch (err) {
			setNewPersonError(err instanceof Error ? err.message : t("createFailed"));
		} finally {
			setNewPersonSaving(false);
		}
	};

	const submitEdit = () => {
		setFormError("");
		const errors: Record<string, string> = {};
		if (!form.type) errors.type = t("requiredField");
		if (!form.subject?.trim()) errors.subject = t("requiredField");
		setFieldErrors(errors);
		if (Object.keys(errors).length > 0) return;
		updateActivity.mutate(
			{
				id,
				data: {
					...form,
					dueDate: form.dueDate || undefined,
					companyId: form.companyId || undefined,
					personId: form.personId || undefined,
					contactName: form.contactName || undefined,
					contactOrgName: form.contactOrgName || undefined,
					contactPhone: form.contactPhone || undefined,
					contactEmail: form.contactEmail || undefined,
					location: form.location || undefined,
					direction: form.direction || undefined,
					outcome: form.outcome || undefined,
				},
			},
			{
				onSuccess: () => {
					setEditOpen(false);
					alert(t("updateSuccess"));
				},
				onError: (err) => {
					setFormError(err instanceof Error ? err.message : t("createFailed"));
				},
			},
		);
	};

	const toggleComplete = () => {
		updateActivity.mutate(
			{
				id,
				data: {
					isCompleted: !activity.isCompleted,
					completedDate: !activity.isCompleted ? new Date().toISOString() : null,
				},
			},
			{
				onSuccess: () => {
					alert(activity.isCompleted ? t("markIncompleteSuccess") : t("markCompleteSuccess"));
				},
			},
		);
	};

	return (
		<div>
			<BackButton fallbackTo="/activities" />
			<PageHeader
				title={activity.subject}
				action={
					<div className={styles.actions}>
						<Button onClick={openEdit}>{t("edit")}</Button>
						<Button variant="secondary" onClick={toggleComplete}>
							{activity.isCompleted ? t("markIncomplete") : t("markComplete")}
						</Button>
						<Button variant="danger" onClick={() => setDeleteOpen(true)}>
							{t("delete")}
						</Button>
					</div>
				}
			/>

			<div className={styles.grid}>
				<Card header={t("generalInfo")}>
					<div className={styles.infoGrid}>
						<Info label={t("type")} value={t(activity.type)} />
						<Info
							label={t("direction")}
							value={activity.direction ? t(activity.direction) : null}
						/>
						<Info
							label={t("outcomeResult")}
							value={activity.outcome ? t(activity.outcome) : null}
						/>
						<Info label={t("subject")} value={activity.subject} />
						<Info
							label={t("dueDate")}
							value={activity.dueDate ? formatDate(activity.dueDate) : "-"}
						/>
						<Info
							label={t("completedDate")}
							value={activity.completedDate ? formatDate(activity.completedDate) : "-"}
						/>
						<Info label={t("owner")} value={activity.ownerName ?? null} />
						{activity.creatorName && activity.creatorName !== activity.ownerName && (
							<Info label={t("createdByLabel")} value={activity.creatorName ?? null} />
						)}
						<div>
							<span className={styles.infoLabel}>{t("status")}</span>
							<Badge variant={activity.isCompleted ? "success" : "warning"}>
								{activity.isCompleted ? t("completed") : t("pending")}
							</Badge>
						</div>
					</div>
				</Card>

				<Card header={t("contact")}>
					<div className={styles.infoGrid}>
						<Info label={t("contactOrgName")} value={activity.contactOrgName} />
						<Info label={t("contactName")} value={activity.contactName} />
						<Info label={t("phone")} value={activity.contactPhone} />
						<Info label={t("email")} value={activity.contactEmail} />
						<Info label={t("location")} value={activity.location} />
					</div>
				</Card>

				<Card header={t("content")}>
					<p className={styles.content}>{activity.content || t("noData")}</p>
				</Card>
			</div>

			<ConfirmDialog
				isOpen={deleteOpen}
				onClose={() => setDeleteOpen(false)}
				onConfirm={() =>
					deleteActivity.mutate(id, {
						onSuccess: () => {
							setDeleteOpen(false);
							alert(t("deleteSuccess"));
						},
					})
				}
				message={t("confirmDelete")}
				isLoading={deleteActivity.isPending}
			/>

			<Modal isOpen={editOpen} onClose={() => setEditOpen(false)} title={t("edit")}>
				{formError && <div className={styles.formError}>{formError}</div>}
				<FormField label={t("type")} required>
					<Select
						options={typeOptions}
						value={form.type}
						onChange={(e) => setForm({ ...form, type: e.target.value as never })}
						error={fieldErrors.type ?? ""}
					/>
				</FormField>
				<FormField label={t("direction")}>
					<Select
						options={[{ value: "", label: "-" }, ...directionOptions]}
						value={form.direction}
						onChange={(e) =>
							setForm({ ...form, direction: e.target.value as ActivityDirection | "" })
						}
					/>
				</FormField>
				<FormField label={t("company")}>
					<SearchableSelect
						value={form.companyId}
						onChange={(val) => {
							setForm({ ...form, companyId: val, personId: "" });
							setNewPersonOpen(false);
							setInitialOptions((prev) => {
								const next = { ...prev };
								delete next.person;
								return next;
							});
						}}
						loadOptions={loadCompanies}
						initialOption={initialOptions.company}
						placeholder={t("selectCompany")}
						clearable
					/>
				</FormField>
				<FormField label={t("contactPersons")}>
					<SearchableSelect
						value={form.personId}
						onChange={(val) => setForm({ ...form, personId: val })}
						loadOptions={loadPersons}
						refetchKey={`${form.companyId}:${personsRefresh}`}
						initialOption={initialOptions.person}
						placeholder={form.companyId ? t("selectPerson") : t("selectCompanyFirst")}
						clearable
						disabled={!form.companyId}
					/>
					{form.companyId && !newPersonOpen && (
						<button
							type="button"
							className={styles.inlineAction}
							onClick={() => setNewPersonOpen(true)}
						>
							+ {t("addNewPerson")}
						</button>
					)}
				</FormField>

				{form.companyId && newPersonOpen && (
					<div className={styles.inlineForm}>
						{newPersonError && <div className={styles.formError}>{newPersonError}</div>}
						<FormField label={t("firstName")} required>
							<Input
								value={newPerson.firstName}
								onChange={(e) => setNewPerson({ ...newPerson, firstName: e.target.value })}
							/>
						</FormField>
						<FormField label={t("lastName")} required>
							<Input
								value={newPerson.lastName}
								onChange={(e) => setNewPerson({ ...newPerson, lastName: e.target.value })}
							/>
						</FormField>
						<FormField label={t("jobTitle")}>
							<Input
								value={newPerson.title}
								onChange={(e) => setNewPerson({ ...newPerson, title: e.target.value })}
							/>
						</FormField>
						<FormField label={t("phone")}>
							<Input
								value={newPerson.phone}
								onChange={(e) => setNewPerson({ ...newPerson, phone: e.target.value })}
							/>
						</FormField>
						<FormField label={t("email")}>
							<Input
								value={newPerson.email}
								onChange={(e) => setNewPerson({ ...newPerson, email: e.target.value })}
							/>
						</FormField>
						<div className={styles.inlineFormActions}>
							<Button
								variant="ghost"
								size="sm"
								onClick={() => {
									setNewPersonOpen(false);
									setNewPersonError("");
								}}
							>
								{t("cancel")}
							</Button>
							<Button size="sm" onClick={saveNewPerson} isLoading={newPersonSaving}>
								{t("save")}
							</Button>
						</div>
					</div>
				)}
				{!form.companyId && !form.personId && (
					<>
						<div className={styles.hint}>{t("noCustomerHint")}</div>
						<FormField label={t("contactOrgName")}>
							<Input
								value={form.contactOrgName}
								onChange={(e) => setForm({ ...form, contactOrgName: e.target.value })}
							/>
						</FormField>
						<FormField label={t("contactName")}>
							<Input
								value={form.contactName}
								onChange={(e) => setForm({ ...form, contactName: e.target.value })}
							/>
						</FormField>
						<FormField label={t("phone")}>
							<Input
								value={form.contactPhone}
								onChange={(e) => setForm({ ...form, contactPhone: e.target.value })}
							/>
						</FormField>
						<FormField label={t("email")}>
							<Input
								value={form.contactEmail}
								onChange={(e) => setForm({ ...form, contactEmail: e.target.value })}
							/>
						</FormField>
					</>
				)}
				<FormField label={t("subject")} required>
					<Input
						value={form.subject}
						onChange={(e) => setForm({ ...form, subject: e.target.value })}
						error={fieldErrors.subject}
					/>
				</FormField>
				<FormField label={t("content")}>
					<Input
						value={form.content}
						onChange={(e) => setForm({ ...form, content: e.target.value })}
					/>
				</FormField>
				<FormField label={t("location")}>
					<Input
						value={form.location}
						onChange={(e) => setForm({ ...form, location: e.target.value })}
					/>
				</FormField>
				<FormField label={t("outcomeResult")}>
					<Select
						options={[{ value: "", label: "-" }, ...outcomeOptions]}
						value={form.outcome}
						onChange={(e) => setForm({ ...form, outcome: e.target.value as ActivityOutcome | "" })}
					/>
				</FormField>
				<FormField label={t("dueDate")}>
					<Input
						type="date"
						value={form.dueDate}
						onChange={(e) => setForm({ ...form, dueDate: e.target.value })}
					/>
				</FormField>
				<div className={styles.modalActions}>
					<Button variant="ghost" onClick={() => setEditOpen(false)}>
						{t("cancel")}
					</Button>
					<Button onClick={submitEdit} isLoading={updateActivity.isPending}>
						{t("update")}
					</Button>
				</div>
			</Modal>
		</div>
	);
}

function Info({ label, value }: { label: string; value: string | null }) {
	return (
		<div>
			<span className={styles.infoLabel}>{label}</span>
			<p className={styles.infoValue}>{value || "-"}</p>
		</div>
	);
}
